import axios, { HttpStatusCode, isAxiosError } from "axios";
import { decodeJwtPayload } from "@/lib/jwt-decode";
import {
  ACCESS_TOKEN_COOKIE_NAME,
  REFRESH_TOKEN_COOKIE_NAME,
  endpoints,
  ISignInResponseBody,
  IUser,
} from "muminsmart-utils";
import { getApiBaseUrl } from "./config";
import {
  clearStoredAuth,
  getStoredAuth,
  getStoredTokens,
  setStoredAuth,
} from "./auth-storage";

export interface AuthSession {
  user: IUser;
}

const getTokenExpiryMs = (token: string): number | null => {
  try {
    const decoded = decodeJwtPayload(token);
    if (decoded?.exp) {
      return decoded.exp * 1000;
    }
  } catch (error) {
    console.error("Error decoding token:", error);
  }

  return null;
};

const TOKEN_REFRESH_BUFFER_MS = 30_000;

type MemoryTokenCache = {
  accessToken?: string;
  refreshToken?: string;
  accessExpiryMs?: number | null;
};

let memoryTokenCache: MemoryTokenCache | null = null;
let memoryTokenCachePromise: Promise<MemoryTokenCache> | null = null;

function clearMemoryTokenCache(): void {
  memoryTokenCache = null;
  memoryTokenCachePromise = null;
}

function setMemoryTokenCache(tokens: {
  accessToken?: string;
  refreshToken?: string;
}): void {
  memoryTokenCache = {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    accessExpiryMs: tokens.accessToken
      ? getTokenExpiryMs(tokens.accessToken)
      : null,
  };
  memoryTokenCachePromise = null;
}

async function getMemoryTokenCache(): Promise<MemoryTokenCache> {
  if (memoryTokenCache) {
    return memoryTokenCache;
  }

  if (!memoryTokenCachePromise) {
    memoryTokenCachePromise = (async () => {
      const tokens = await getStoredTokens();
      setMemoryTokenCache(tokens);
      return memoryTokenCache ?? {};
    })();
  }

  return memoryTokenCachePromise;
}

const isTokenExpired = (token: string): boolean => {
  const expiry = getTokenExpiryMs(token);
  return expiry === null || Date.now() >= expiry;
};

const buildCookieHeader = (
  accessToken?: string,
  refreshToken?: string,
): string | undefined => {
  const parts: string[] = [];

  if (accessToken) {
    parts.push(`${ACCESS_TOKEN_COOKIE_NAME}=${accessToken}`);
  }

  if (refreshToken) {
    parts.push(`${REFRESH_TOKEN_COOKIE_NAME}=${refreshToken}`);
  }

  return parts.length > 0 ? parts.join("; ") : undefined;
};

let refreshInFlight: Promise<ISignInResponseBody | null> | null = null;

async function performRefreshAuthTokens(
  refreshToken: string,
): Promise<ISignInResponseBody | null> {
  try {
    const response = await axios.post<ISignInResponseBody>(
      `${getApiBaseUrl()}${endpoints.auth.refresh}`,
      {},
      {
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${refreshToken}`,
          Cookie: buildCookieHeader(undefined, refreshToken),
        },
      },
    );

    if (response.status !== HttpStatusCode.Ok) {
      throw new Error("Failed to refresh access token");
    }

    await setStoredAuth({
      accessToken: response.data.accessToken,
      refreshToken: response.data.refreshToken,
      user: response.data.user,
    });
    setMemoryTokenCache({
      accessToken: response.data.accessToken,
      refreshToken: response.data.refreshToken,
    });

    return response.data;
  } catch (error) {
    console.error("Refresh token failed", error);
    await clearStoredAuth();
    clearMemoryTokenCache();
    return null;
  }
}

function refreshAuthTokens(
  refreshToken: string,
): Promise<ISignInResponseBody | null> {
  if (!refreshInFlight) {
    refreshInFlight = performRefreshAuthTokens(refreshToken).finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
}

export async function ensureValidAccessToken(): Promise<string | null> {
  const cache = await getMemoryTokenCache();

  if (
    cache.accessToken &&
    cache.accessExpiryMs &&
    Date.now() < cache.accessExpiryMs - TOKEN_REFRESH_BUFFER_MS
  ) {
    return cache.accessToken;
  }

  const { accessToken, refreshToken } = cache.accessToken
    ? cache
    : await getMemoryTokenCache();

  if (accessToken && !isTokenExpired(accessToken)) {
    setMemoryTokenCache({ accessToken, refreshToken });
    return accessToken;
  }

  if (!refreshToken || isTokenExpired(refreshToken)) {
    await clearStoredAuth();
    clearMemoryTokenCache();
    return null;
  }

  const refreshed = await refreshAuthTokens(refreshToken);
  return refreshed?.accessToken ?? null;
}

export async function getAuthRequestTokens(): Promise<{
  accessToken: string | null;
  refreshToken?: string;
}> {
  const accessToken = await ensureValidAccessToken();
  const cache = await getMemoryTokenCache();

  return {
    accessToken,
    refreshToken: cache.refreshToken,
  };
}

export async function getSession(): Promise<AuthSession | null> {
  const stored = await getStoredAuth();
  if (!stored) {
    return null;
  }

  const accessToken = await ensureValidAccessToken();
  if (!accessToken) {
    return null;
  }

  const latest = await getStoredAuth();
  if (!latest) {
    return null;
  }

  return { user: latest.user };
}

export async function login(payload: {
  email?: string;
  mobile?: string;
  password: string;
}): Promise<AuthSession> {
  try {
    const response = await axios.post<ISignInResponseBody>(
      `${getApiBaseUrl()}${endpoints.auth.login}`,
      payload,
      {
        headers: {
          accept: "application/json",
        },
      },
    );

    if (
      response.status !== HttpStatusCode.Ok ||
      !response.data.accessToken ||
      !response.data.refreshToken
    ) {
      throw new Error("Login failed");
    }

    await setStoredAuth({
      accessToken: response.data.accessToken,
      refreshToken: response.data.refreshToken,
      user: response.data.user,
    });
    setMemoryTokenCache({
      accessToken: response.data.accessToken,
      refreshToken: response.data.refreshToken,
    });

    return { user: response.data.user };
  } catch (error) {
    if (isAxiosError(error)) {
      if (!error.response) {
        const isNetworkError =
          error.code === "ERR_NETWORK" || error.message === "Network Error";
        throw new Error(
          isNetworkError
            ? `Cannot reach ${getApiBaseUrl()}. Check that the server is reachable from the device.`
            : error.message,
        );
      }

      throw new Error(error.response?.data?.message ?? "Login failed");
    }

    throw error;
  }
}

export async function logout(): Promise<void> {
  const { accessToken, refreshToken } = await getStoredTokens();

  try {
    await axios.post(
      `${getApiBaseUrl()}${endpoints.auth.logout}`,
      {},
      {
        headers: {
          accept: "application/json",
          Cookie: buildCookieHeader(accessToken, refreshToken),
        },
      },
    );
  } catch (error) {
    console.error("Backend logout failed", error);
  } finally {
    await clearStoredAuth();
    clearMemoryTokenCache();
  }
}
